// LoginPage — staff path (email + password + optional TOTP) and
// guardian path (phone -> OTP request -> OTP verify) on one screen.
//
// Single-column layout at every breakpoint. The elevated card carries
// the focus; the page backdrop is a clean off-white with a faint
// crimson glow in the top-left and a cooler accent in the bottom-right
// (both come from PageShell). No two-column marketing-style hero -
// keeps the user's eye where the action is.
//
// State machines and API calls are byte-identical to the previous
// version; presentation only.

import {
  type FormEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Button,
  Card,
  PageShell,
  Segmented,
  Spinner,
  Stack,
  TextField,
  tokens,
} from "@sms/design-system";
import type { Locale } from "@sms/i18n";
import {
  type Client,
  ConflictError,
  type Me,
  RateLimitError,
  TOTPRequiredError,
  UnauthorizedError,
  isApiError,
} from "@sms/sdk";

import { makeT } from "../i18n";

type Tab = "staff" | "guardian";

type StaffState =
  | { kind: "form" }
  | { kind: "totp_required"; email: string; password: string; schoolSlug?: string }
  | { kind: "submitting" }
  | { kind: "error"; message: string };

type GuardianState =
  | { kind: "phone" }
  | { kind: "otp_sent"; phone: string }
  | { kind: "submitting" }
  | { kind: "error"; message: string; phone?: string };

interface LoginPageProps {
  client: Pick<
    Client,
    "loginStaff" | "requestGuardianOTP" | "verifyGuardianOTP"
  >;
  locale: Locale;
  onSignedIn: (me: Me) => void;
}

export function LoginPage({ client, locale, onSignedIn }: LoginPageProps) {
  const t = makeT(locale);
  const [tab, setTab] = useState<Tab>("guardian");

  return (
    <PageShell
      brand={<BrandMark />}
      title={t("app.name")}
      footer={<TrustNote text={t("login.privacy_note")} />}
    >
      <Card variant="elevated" padding={6}>
        <Stack gap={5}>
          <Segmented<Tab>
            ariaLabel="Login type"
            value={tab}
            onChange={setTab}
            options={[
              { value: "guardian", label: t("login.tab_guardian") },
              { value: "staff",    label: t("login.tab_staff")    },
            ]}
          />
          {tab === "guardian" ? (
            <GuardianForm client={client} t={t} onSignedIn={onSignedIn} />
          ) : (
            <StaffForm client={client} t={t} onSignedIn={onSignedIn} />
          )}
        </Stack>
      </Card>
    </PageShell>
  );
}

// ── Brand mark + trust note ───────────────────────────────────────────

function BrandMark() {
  // Restrained mark: 56px brand-coloured tile, no gradient, no shadow,
  // a single 2px inner ring for depth. Reads as institutional, not
  // SaaS-startup. Glyph is a stylised graduation/school motif.
  return (
    <div style={brandMarkStyle}>
      <svg
        viewBox="0 0 24 24"
        width="28"
        height="28"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {/* Stylised open book — universal "school" mark. */}
        <path d="M2 7l10 4 10-4-10-4-10 4z" />
        <path d="M6 9.5v6c0 .5.3.9.8 1.1A12 12 0 0 0 12 18a12 12 0 0 0 5.2-1.4c.5-.2.8-.6.8-1.1v-6" />
      </svg>
    </div>
  );
}

function TrustNote({ text }: { text: string }) {
  return (
    <span style={trustNoteStyle}>
      <svg
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{ flexShrink: 0 }}
      >
        <rect x="5" y="11" width="14" height="9" rx="2" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
      </svg>
      <span>{text}</span>
    </span>
  );
}

// ── Guardian form ─────────────────────────────────────────────────────

function GuardianForm({
  client,
  t,
  onSignedIn,
}: {
  client: Pick<Client, "requestGuardianOTP" | "verifyGuardianOTP">;
  t: (k: string, v?: Record<string, string>) => string;
  onSignedIn: (me: Me) => void;
}) {
  const [state, setState] = useState<GuardianState>({ kind: "phone" });
  const [phone, setPhone] = useState("");
  const [otp, setOTP] = useState("");
  const otpRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (state.kind === "otp_sent") otpRef.current?.focus();
  }, [state.kind]);

  const requestOTP = async (e: FormEvent) => {
    e.preventDefault();
    setState({ kind: "submitting" });
    try {
      await client.requestGuardianOTP(phone);
      setState({ kind: "otp_sent", phone });
    } catch (err) {
      setState({ kind: "error", message: explain(err, t), phone });
    }
  };

  const verifyOTP = async (e: FormEvent) => {
    e.preventDefault();
    setState({ kind: "submitting" });
    try {
      const me = await client.verifyGuardianOTP(phone, otp);
      onSignedIn(me);
    } catch (err) {
      setState({ kind: "error", message: explain(err, t), phone });
    }
  };

  if (state.kind === "submitting") {
    return <LoadingPanel label={t("common.loading")} />;
  }

  if (state.kind === "phone" || state.kind === "error") {
    const errMsg = state.kind === "error" ? state.message : undefined;
    return (
      <form onSubmit={requestOTP}>
        <Stack gap={4}>
          <TextField
            label={t("login.phone")}
            hint={t("login.phone_hint")}
            error={errMsg}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            autoFocus
            required
            placeholder="98XXXXXXXX"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            prefix="+977"
          />
          <Button type="submit" size="lg" fullWidth>
            {t("login.request_otp")}
          </Button>
        </Stack>
      </form>
    );
  }

  return (
    <form onSubmit={verifyOTP}>
      <Stack gap={4}>
        <Alert intent="info">
          {t("login.otp_sent_to", { phone: state.phone })}
        </Alert>
        <TextField
          ref={otpRef}
          label={t("login.otp_code")}
          hint={t("login.otp_hint")}
          variant="code"
          inputMode="numeric"
          pattern="[0-9]{6}"
          autoComplete="one-time-code"
          required
          maxLength={6}
          value={otp}
          onChange={(e) => setOTP(e.target.value.replace(/\D/g, ""))}
        />
        <Button type="submit" size="lg" fullWidth>
          {t("login.submit_otp")}
        </Button>
      </Stack>
    </form>
  );
}

// ── Staff form ────────────────────────────────────────────────────────

function StaffForm({
  client,
  t,
  onSignedIn,
}: {
  client: Pick<Client, "loginStaff">;
  t: (k: string, v?: Record<string, string>) => string;
  onSignedIn: (me: Me) => void;
}) {
  const [state, setState] = useState<StaffState>({ kind: "form" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [schoolSlug, setSchoolSlug] = useState("");
  const [totp, setTotp] = useState("");
  const totpRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (state.kind === "totp_required") totpRef.current?.focus();
  }, [state.kind]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const pendingArgs = {
      email,
      password,
      ...(schoolSlug ? { schoolSlug } : {}),
      ...(state.kind === "totp_required" || totp ? { totpCode: totp } : {}),
    };
    setState({ kind: "submitting" });
    try {
      const me = await client.loginStaff(pendingArgs);
      onSignedIn(me);
    } catch (err) {
      if (err instanceof TOTPRequiredError) {
        setState({ kind: "totp_required", email, password, schoolSlug });
        return;
      }
      if (err instanceof ConflictError) {
        setState({ kind: "error", message: t("login.school_slug_required") });
        return;
      }
      setState({ kind: "error", message: explain(err, t) });
    }
  };

  const askingForTotp = state.kind === "totp_required";
  const submitting = state.kind === "submitting";
  const errMsg = state.kind === "error" ? state.message : undefined;

  return (
    <form onSubmit={submit}>
      <Stack gap={4}>
        <TextField
          label={t("login.email")}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label={t("login.password")}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <TextField
          label={t("login.school_slug_optional")}
          hint={t("login.school_hint")}
          autoComplete="organization"
          value={schoolSlug}
          onChange={(e) => setSchoolSlug(e.target.value)}
        />
        {askingForTotp && (
          <TextField
            ref={totpRef}
            label={t("login.totp_code")}
            variant="code"
            inputMode="numeric"
            pattern="[0-9]{6}"
            autoComplete="one-time-code"
            required
            maxLength={6}
            value={totp}
            onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
          />
        )}
        {errMsg && <Alert intent="danger">{errMsg}</Alert>}
        <Button type="submit" size="lg" fullWidth disabled={submitting}>
          {submitting ? (
            <Stack direction="row" gap={2} align="center" justify="center">
              <Spinner size={18} label={t("common.loading")} />
              <span>{t("common.loading")}</span>
            </Stack>
          ) : (
            t("actions.login")
          )}
        </Button>
      </Stack>
    </form>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

function LoadingPanel({ label }: { label: string }) {
  return (
    <Stack
      gap={3}
      align="center"
      justify="center"
      style={{ padding: `${tokens.space[8]} 0` }}
    >
      <Spinner size={32} label={label} />
      <p style={mutedStyle}>{label}</p>
    </Stack>
  );
}

function explain(err: unknown, t: (k: string) => string): string {
  if (err instanceof UnauthorizedError) return t("login.invalid_credentials");
  if (err instanceof RateLimitError) return t("login.rate_limited");
  if (isApiError(err) && err.problem.detail) return err.problem.detail;
  return t("common.errorGeneric");
}

// ── Styles ────────────────────────────────────────────────────────────

const brandMarkStyle: React.CSSProperties = {
  width: 56,
  height: 56,
  borderRadius: 16,
  background: tokens.color.brand[500],
  color: tokens.color.neutral[0],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  // Single restrained ring + tiny shadow. No gradients, no glow halos.
  boxShadow:
    "inset 0 0 0 2px rgba(255, 255, 255, 0.12), 0 1px 2px rgba(15, 23, 42, 0.08)",
};

const trustNoteStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: tokens.space[2],
  fontSize: tokens.type.size.xs,
  color: tokens.color.semantic.textMuted,
  lineHeight: 1.5,
};

const mutedStyle: React.CSSProperties = {
  fontSize: tokens.type.size.sm,
  color: tokens.color.semantic.textMuted,
};
